/**
 * TodoDetailView — Rich metadata display and editing for individual todos.
 *
 * UX behavior:
 * - Edit mode: clicking an editable field enters edit mode for the entire view
 * - Multiple fields can be edited in a single session without auto-saving
 * - Save button: persists ALL pending changes via onUpdate, exits edit mode
 * - Cancel button: rolls back ALL changes to pre-edit state, exits edit mode
 * - Collapse = cancel: collapsing mid-edit triggers the same rollback
 * - No auto-save while in edit mode
 *
 * Displays: description, due date/time, priority, assignment (owner display name),
 * recurrence rules, notification settings, vanishing config, close reason, complete button.
 * Source field (source_plugin) is intentionally excluded from display (Issue #4).
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Todo, TodoPriority } from '@/infra/types/domain';
import { useHouseholdMembers } from '@/features/household/hooks';
import { selectDayStartHour, selectCurrentUserId } from '@/features/household/selectors';
import { MentionInput, MentionText } from '@/features/mentions';
import { usePluginRegistry } from '@/infra/plugin-registry';
import { TodoDetailSlot } from '@/infra/plugin-registry/types';
import { RecurrencePicker } from '../recurrence/RecurrencePicker';
import { DateTimeField } from '@/components/DateTimeField';
import { TagEditor } from './TagEditor';
import { TagChip } from './TagChip';
import { tagKey } from '../tags';
import { markBlockedRequested, claimTodoRequested } from '../ducks';
import { evalValidationRule } from '@/features/predicate/validation';

// DetailSlot renders every enabled plugin's contribution for a named slot.
// Slots are injected at fixed anchors inside TodoDetailView so the base
// layout is stable; plugins add below-title project info, below-assignment
// extensions, etc. Each renderer receives the same props as a full
// TodoRenderer, so plugins can reuse existing components.
function DetailSlot({
  slot,
  todo,
  onUpdate,
}: {
  slot: TodoDetailSlot;
  todo: Todo;
  onUpdate: ((update: { id: string; [key: string]: unknown }) => void) | undefined;
}) {
  const registry = usePluginRegistry();
  const renderers = registry.getDetailSlotRenderers(slot);
  if (renderers.length === 0) return null;
  return (
    <>
      {renderers.map((Renderer, i) => (
        <Renderer key={i} todo={todo} onUpdate={onUpdate} />
      ))}
    </>
  );
}

/**
 * Auto-link plain URLs, email addresses, and phone numbers that aren't already
 * inside markdown link syntax. Runs as a pre-processing step before ReactMarkdown
 * so that these patterns become clickable links in the rendered output.
 */
function autoLinkText(text: string): string {
  // Don't touch text that's already inside []() markdown links
  // Process line-by-line to keep it simple
  return text.replace(
    // Match URLs not already in markdown link syntax
    /(?<!\]\()(?<!\()(https?:\/\/[^\s<>)\]]+)/g,
    '[$1]($1)',
  ).replace(
    // Match email addresses not already in markdown link syntax
    /(?<!\]\()(?<!\()([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    '[$1](mailto:$1)',
  ).replace(
    // Match phone numbers (common US formats)
    /(?<!\]\()(?<!\()(\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})\b/g,
    '[$1](tel:$1)',
  );
}

export interface TodoDetailViewProps {
  todo: Todo;
  onUpdate?: (update: { id: string; [key: string]: unknown }) => void;
  /** Called after a confirmed action (Save committed, Delete confirmed,
   * scope picked) so the host can dismiss the modal. NOT called for X /
   * Esc / backdrop — those go through the host's own dirty-check path. */
  onClose?: () => void;
  /** Notifies the host when there are unsaved draft changes. The host uses
   * this to gate dismiss affordances (X / Esc / backdrop) behind a
   * confirm — Save itself just calls onClose, no prompt needed. */
  onDirtyChange?: (dirty: boolean) => void;
}

/** Convert RRULE-style recurrence_rule to human-readable text. */
function formatRecurrence(rule: string | null | undefined): string {
  if (!rule) return 'No recurrence';

  const freq = rule.match(/FREQ=(\w+)/)?.[1];
  const byDay = rule.match(/BYDAY=(\w+)/)?.[1];
  const byHour = rule.match(/BYHOUR=(\d+)/)?.[1];
  const byMinute = rule.match(/BYMINUTE=(\d+)/)?.[1];

  const knownKeys = new Set(['FREQ', 'BYDAY', 'BYHOUR', 'BYMINUTE']);
  const parts = rule.split(';').map((p) => p.split('=')[0]);
  const hasUnknown = parts.some((p) => !knownKeys.has(p));
  if (hasUnknown) return rule;

  let timeStr = '';
  if (byHour) {
    const hour = parseInt(byHour, 10);
    const minute = byMinute ? parseInt(byMinute, 10) : 0;
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    timeStr = ` at ${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
  }

  const dayMap: Record<string, string> = {
    MO: 'Monday', TU: 'Tuesday', WE: 'Wednesday', TH: 'Thursday',
    FR: 'Friday', SA: 'Saturday', SU: 'Sunday',
  };

  switch (freq) {
    case 'DAILY':
      return `Every day${timeStr}`;
    case 'WEEKLY':
      return `Every ${byDay ? dayMap[byDay] || byDay : 'week'}${timeStr}`;
    case 'MONTHLY':
      return `Every month${timeStr}`;
    case 'YEARLY':
      return `Every year${timeStr}`;
    default:
      return rule;
  }
}

/**
 * The UTC instant corresponding to 10:00 in the user's local wall clock
 * TODAY. Used to seed the datetime-local picker when the todo has no
 * due_at yet. `toISOString()` serializes the instant with a real Z
 * offset — the server stores a correct UTC instant, and the display
 * layer renders it back as 10 AM local via `new Date(...)`.
 */
function defaultDueAt10amLocal(): string {
  const now = new Date();
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    10, 0, 0, 0,
  ).toISOString();
}

/**
 * Format a UTC ISO instant for a `<input type="datetime-local">`, which
 * expects `YYYY-MM-DDTHH:MM` in the user's LOCAL wall clock (no offset).
 * The inverse of `localInputValueToUtcIso`: takes an instant, shows the
 * wall-clock time the user actually sees on their clock.
 */
function utcIsoToLocalInputValue(utcIso: string | null): string {
  if (!utcIso) return '';
  const d = new Date(utcIso);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${da}T${hh}:${mm}`;
}

/**
 * Convert a datetime-local input value (local wall clock, no TZ) to the
 * UTC ISO instant that the server should actually store. Historical bug:
 * earlier code appended `:00Z` to the raw local string, which made the
 * server interpret the user's wall clock as UTC — picking 2 PM stored
 * 10 AM EDT. This routes through the browser's local-TZ Date constructor
 * so the conversion is correct regardless of the user's offset / DST.
 */
function localInputValueToUtcIso(local: string): string {
  const [datePart, timePart] = local.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0).toISOString();
}

/** Format due date to human-readable string (includes year).
 * `dayStartHour` is the user's logical-day boundary — we shift both the
 * due instant and "now" back by that many hours before comparing
 * calendar dates, so a 2 AM due time still reads "Today" when the user's
 * day starts at 6 AM and the calendar-day rollover hasn't fired yet for
 * them. Without the shift, near-midnight items silently misbucket. */
function formatDueDate(dueAt: string | null, dayStartHour: number): string {
  if (!dueAt) return 'No due date';

  const date = new Date(dueAt);
  if (isNaN(date.getTime())) return dueAt;

  try {
    const now = new Date();
    const shiftMs = dayStartHour * 3_600_000;
    const dateLogical = new Date(date.getTime() - shiftMs);
    const nowLogical = new Date(now.getTime() - shiftMs);
    const isToday =
      dateLogical.getFullYear() === nowLogical.getFullYear() &&
      dateLogical.getMonth() === nowLogical.getMonth() &&
      dateLogical.getDate() === nowLogical.getDate();

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    if (isToday) {
      return `Today at ${timeStr}`;
    }

    const dateStr = date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    return `${dateStr} at ${timeStr}`;
  } catch {
    return dueAt;
  }
}

type NotifyUnit = 'minutes' | 'hours' | 'days';

const UNIT_MS: Record<NotifyUnit, number> = {
  minutes: 60_000,
  hours: 3_600_000,
  days: 86_400_000,
};

/**
 * Decompose notify_before (ms) into the largest whole unit it divides evenly,
 * falling back to minutes. Used by the picker so a value like 7_200_000 reads
 * as "2 hours" instead of "120 minutes".
 */
function msToAmountUnit(ms: number): { amount: number; unit: NotifyUnit } {
  if (ms <= 0) return { amount: 0, unit: 'minutes' };
  if (ms % UNIT_MS.days === 0) return { amount: ms / UNIT_MS.days, unit: 'days' };
  if (ms % UNIT_MS.hours === 0) return { amount: ms / UNIT_MS.hours, unit: 'hours' };
  return { amount: Math.max(1, Math.round(ms / UNIT_MS.minutes)), unit: 'minutes' };
}

function amountUnitToMs(amount: number, unit: NotifyUnit): number {
  return amount * UNIT_MS[unit];
}

/** Format notification timing. notify_before is in milliseconds.
 * No "default to 2 minutes" cover-up — if the row arrives enabled with
 * notify_before=0, the seed in the edit modal promotes that to 120000
 * and the user sees the truth here ("0 minutes before") in any
 * read-only renderer (TodoItem, list cells) until they Save. */
function formatNotification(enabled?: boolean, notifyBeforeMs?: number): string {
  if (!enabled) return 'Disabled';
  const ms = notifyBeforeMs ?? 0;
  const { amount, unit } = msToAmountUnit(ms);
  const label = amount === 1 ? unit.replace(/s$/, '') : unit;
  return `Notify ${amount} ${label} before`;
}

const PRIORITY_OPTIONS: TodoPriority[] = ['urgent', 'high', 'normal', 'low'];

/**
 * Tag sets are equal when they hold the same case-insensitive names,
 * regardless of order — the server stores a set, so reordering or recasing
 * isn't a meaningful edit and must not dirty the Save button.
 */
function sameTagSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b.map(tagKey));
  return a.every((t) => setB.has(tagKey(t)));
}

/**
 * TodoDetailView — public component for rendering todo detail/editing UI.
 *
 * Edit mode: user clicks an editable field → enters edit mode. All changes are
 * tracked locally. User must explicitly Save (persist all) or Cancel (rollback all).
 */
export function TodoDetailView({ todo, onUpdate, onClose, onDirtyChange }: TodoDetailViewProps) {
  const dispatch = useDispatch();
  const dayStartHour = useSelector((state: any) => selectDayStartHour(state));
  const [editMode, setEditMode] = useState(false);
  const [editingFields, setEditingFields] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  // Inline confirm state for the Delete button. 'confirm' reveals either a
  // two-button (Delete / Cancel) or three-button (Just this one / Entire
  // series / Cancel) bar in place of the action bar.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Inline scope picker for Save when the user changed due_at or owner on a
  // recurring todo. We stash the pending update here and surface a three-
  // button bar (Just this one / Entire series / Cancel) before sending it
  // to the server.
  const [pendingScopedUpdate, setPendingScopedUpdate] = useState<
    { id: string; [key: string]: unknown } | null
  >(null);

  // ── Local edit state — every editable field is a draft until Save ──
  // The modal-as-edit-session model: nothing hits the wire until the user
  // clicks Save (which prompts for instance/series scope on a recurring
  // todo) or Cancel (which discards). Auto-syncing from the todo prop
  // would clobber in-progress edits when the WS broadcast lands, so we
  // only re-seed when the row identity changes.
  const [localTitle, setLocalTitle] = useState(todo.title);
  const [localDescription, setLocalDescription] = useState(todo.description || '');
  const [localPriority, setLocalPriority] = useState<TodoPriority>(todo.priority);
  const [localDueAt, setLocalDueAt] = useState<string | null>(todo.due_at);
  // True only once the user actively mutates the due-date control (picks a
  // value or clears it). `localDueAt` is seeded to today@10am purely to
  // position the picker when entering edit mode via another field, so its
  // value alone can't distinguish "user chose a due date" from "presentational
  // seed". Without this gate, editing only the title of an undated (Inbox)
  // todo would persist the seed as a real due_at and yank it into Today.
  const [dueAtTouched, setDueAtTouched] = useState(false);
  const [localRecurrenceRule, setLocalRecurrenceRule] = useState<string | null | undefined>(todo.recurrence_rule);
  const [localOwnerId, setLocalOwnerId] = useState<string>(todo.owner_id || '');
  const [localNotifyEnabled, setLocalNotifyEnabled] = useState<boolean>(!!todo.notify_enabled);
  const [localNotifyBefore, setLocalNotifyBefore] = useState<number>(todo.notify_before ?? 0);
  const [localVanishingEnabled, setLocalVanishingEnabled] = useState<boolean>(!!todo.vanishing_enabled);
  const [localVisibility, setLocalVisibility] = useState<string>(todo.visibility ?? 'public');
  const [localTags, setLocalTags] = useState<string[]>(todo.tags ?? []);

  // Reset draft + UI state when the modal opens a different todo. We
  // intentionally do NOT depend on the full `todo` object — that would
  // wipe in-progress edits every time the WS broadcast updates the row.
  useEffect(() => {
    setEditMode(false);
    setEditingFields(new Set());
    setDueAtTouched(false);
    setDeleting(false);
    setConfirmingDelete(false);
    setPendingScopedUpdate(null);
    setLocalTitle(todo.title);
    setLocalDescription(todo.description || '');
    setLocalPriority(todo.priority);
    setLocalDueAt(todo.due_at);
    setLocalRecurrenceRule(todo.recurrence_rule);
    setLocalOwnerId(todo.owner_id || '');
    setLocalNotifyEnabled(!!todo.notify_enabled);
    // Legacy rows can land here with notify_enabled=true but
    // notify_before=0 (the broken-modal-write bug from before the
    // edit-session refactor + the spawnNextRecurrence drop-on-the-floor).
    // The display layer used to mask it as "2 minutes" — which then
    // silently became 0 on Save because the local state was 0 too. Seed
    // a sane default so the UI is honest and Save can commit the fix.
    const seedNotifyBefore =
      todo.notify_enabled && (!todo.notify_before || todo.notify_before === 0)
        ? 120000
        : (todo.notify_before ?? 0);
    setLocalNotifyBefore(seedNotifyBefore);
    setLocalVanishingEnabled(!!todo.vanishing_enabled);
    setLocalVisibility(todo.visibility ?? 'public');
    setLocalTags(todo.tags ?? []);
  }, [todo.id]); // intentional: re-seed only on identity change, not every WS update

  const householdMembers = useHouseholdMembers();

  // Deduplicate members by id to prevent duplicate options in dropdown
  const uniqueMembers = useMemo(() => {
    const seen = new Set<string>();
    return (householdMembers || []).filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [householdMembers]);

  const getOwnerName = useCallback(
    (ownerId: string): string => {
      const member = uniqueMembers.find((m) => m.id === ownerId);
      return member?.display_name || '(Unassigned)';
    },
    [uniqueMembers],
  );

  const currentUserId = useSelector(selectCurrentUserId);

  // Who may change the assignee — computed identically for every todo so the
  // field behaves the same for regular and project tasks (claimability, not
  // project-ness, gates it). Mirrors the server's assertTodoClaimable:
  //  - the owner can always reassign their own row (via UpdateTodo owner_id);
  //  - a non-owner can CLAIM a visible row that is project-bound, recurring, or
  //    undated (dated non-recurring non-project rows are inbox-drag-only, 409).
  const isOwner = !!currentUserId && todo.owner_id === currentUserId;
  const claimableByMe =
    !!currentUserId &&
    !isOwner &&
    todo.visibility !== 'private' &&
    (todo.project_id != null || !!todo.recurrence_rule || todo.due_at == null);
  const ownerChanged = localOwnerId !== (todo.owner_id || '');
  // Assignment is editable via the owner-update path (onUpdate) or the claim
  // path — the latter works even when the rest of a partner-owned row is frozen.
  const canEditAssignment = !!onUpdate || claimableByMe;
  // A pending change is committable when fields are editable (onUpdate) or there
  // is a claimable owner change to dispatch (no onUpdate needed for that path).
  const canCommit = !!onUpdate || (claimableByMe && ownerChanged);

  // Exit owner_id editing when members transition from non-empty to empty
  const prevMemberCountRef = useRef(uniqueMembers.length);
  useEffect(() => {
    if (prevMemberCountRef.current > 0 && uniqueMembers.length === 0 && editingFields.has('owner_id')) {
      setEditingFields((prev) => {
        const next = new Set(prev);
        next.delete('owner_id');
        return next;
      });
    }
    prevMemberCountRef.current = uniqueMembers.length;
  }, [uniqueMembers.length, editingFields]);

  // ── Edit mode actions ──

  /** Enter edit mode and open a specific field for editing. */
  const startEdit = useCallback(
    (field: string) => {
      // Assignment can be opened even when the rest of the modal is read-only
      // (a claimable partner-owned row); every other field requires onUpdate.
      if (!onUpdate && !(field === 'owner_id' && canEditAssignment)) return;
      if (!editMode) {
        // Snapshot current todo values when entering edit mode.
        // For due_at, if the todo has none yet, seed a sensible default
        // (today at 10:00 local) so the picker opens on a useful time
        // rather than "00:00". Existing due_at is preserved verbatim.
        setLocalTitle(todo.title);
        setLocalDescription(todo.description || '');
        setLocalPriority(todo.priority);
        setLocalDueAt(todo.due_at ?? defaultDueAt10amLocal());
        setLocalRecurrenceRule(todo.recurrence_rule);
        setLocalOwnerId(todo.owner_id || '');
      } else if (field === 'due_at' && !todo.due_at && !localDueAt) {
        // Already in edit mode for other fields; user is now opening
        // due_at for the first time — seed the same default.
        setLocalDueAt(defaultDueAt10amLocal());
      }
      setEditMode(true);
      setEditingFields((prev) => new Set(prev).add(field));
    },
    [onUpdate, canEditAssignment, editMode, todo, localDueAt],
  );

  /** Diff of every editable field — drives Save's enabled state and
   * the payload built in handleSave. Centralised here so adding a new
   * field is one tuple, not three callsites. */
  const pendingUpdate = useMemo<Record<string, unknown>>(() => {
    const u: Record<string, unknown> = {};
    if (localTitle !== todo.title) u.title = localTitle;
    if (localDescription !== (todo.description || '')) u.description = localDescription;
    if (localPriority !== todo.priority) u.priority = localPriority;
    if (dueAtTouched && localDueAt !== todo.due_at) u.due_at = localDueAt;
    if (localRecurrenceRule !== todo.recurrence_rule) u.recurrence_rule = localRecurrenceRule;
    if (localOwnerId !== (todo.owner_id || '')) u.owner_id = localOwnerId;
    if (localNotifyEnabled !== !!todo.notify_enabled) u.notify_enabled = localNotifyEnabled;
    if (localNotifyBefore !== (todo.notify_before ?? 0)) u.notify_before = localNotifyBefore;
    if (localVanishingEnabled !== !!todo.vanishing_enabled) u.vanishing_enabled = localVanishingEnabled;
    if (localVisibility !== (todo.visibility ?? 'public')) u.visibility = localVisibility;
    // Tags replace-set on the server; send the full desired array only when
    // the set actually changed (order/case-insensitive). Diffs stay clean.
    if (!sameTagSet(localTags, todo.tags ?? [])) u.tags = localTags;
    return u;
  }, [
    todo,
    localTitle,
    localDescription,
    localPriority,
    localDueAt,
    dueAtTouched,
    localRecurrenceRule,
    localOwnerId,
    localNotifyEnabled,
    localNotifyBefore,
    localVanishingEnabled,
    localVisibility,
    localTags,
  ]);
  const hasPendingChanges = Object.keys(pendingUpdate).length > 0;

  // Surface dirty state to the host modal so it can gate X / Esc /
  // backdrop dismiss behind a confirm. Reset to false on unmount in case
  // the host swaps to a different todo without unmounting the modal.
  useEffect(() => {
    onDirtyChange?.(hasPendingChanges);
    return () => onDirtyChange?.(false);
  }, [hasPendingChanges, onDirtyChange]);

  /** Save batches every pending diff into a single onUpdate. For recurring
   * todos with any change, we defer to the inline scope picker so the user
   * explicitly picks "Just this one" vs "Entire series" once for the whole
   * batch — never per-field, never silent. Save (and the scope-picker
   * commit) close the modal so the editing session has a clean exit. */
  const handleSave = useCallback(() => {
    if (!canCommit) {
      onClose?.();
      return;
    }
    if (!hasPendingChanges) {
      onClose?.();
      return;
    }

    // Claiming a row the current user doesn't own: route the owner change
    // through ClaimTodo (the owner-gated UpdateTodo would 403). Claim is
    // series-scoped, matching the drag-to-member path — no instance/series
    // prompt. A non-owner can't edit any other field, so there is nothing else
    // to persist. Handled before the onUpdate gate, which is absent here.
    if (ownerChanged && claimableByMe) {
      if (localOwnerId) dispatch(claimTodoRequested(todo.id, localOwnerId));
      setEditMode(false);
      setEditingFields(new Set());
      setDueAtTouched(false);
      onClose?.();
      return;
    }

    // Owner-update path (rows you own): batch every field — including owner_id —
    // through onUpdate. Recurring rows pick instance/series first.
    if (!onUpdate) {
      onClose?.();
      return;
    }
    const update = { id: todo.id, ...pendingUpdate };
    if (todo.recurrence_rule) {
      setPendingScopedUpdate(update);
      return;
    }
    onUpdate(update);
    setEditMode(false);
    setEditingFields(new Set());
    setDueAtTouched(false);
    onClose?.();
  }, [canCommit, onUpdate, onClose, hasPendingChanges, ownerChanged, claimableByMe, localOwnerId, dispatch, pendingUpdate, todo.id, todo.recurrence_rule]);

  /** Send the pending update with the chosen scope and close the modal. */
  const commitScopedUpdate = useCallback(
    (scope: 'instance' | 'series') => {
      if (!onUpdate || !pendingScopedUpdate) return;
      onUpdate({ ...pendingScopedUpdate, _scope: scope });
      setPendingScopedUpdate(null);
      setEditMode(false);
      setEditingFields(new Set());
      setDueAtTouched(false);
      onClose?.();
    },
    [onUpdate, onClose, pendingScopedUpdate],
  );

  const cancelScopedUpdate = useCallback(() => {
    setPendingScopedUpdate(null);
  }, []);

  // Cancel on unmount — when parent unmounts this component mid-edit, discard
  // local UI state. We DON'T call onClose here: unmount means the host
  // already closed (or remounted on a different todo); calling onClose would
  // re-trigger that path.
  useEffect(() => {
    return () => {
      setEditMode(false);
      setEditingFields(new Set());
      setDueAtTouched(false);
      setPendingScopedUpdate(null);
    };
  }, []);


  // ── Non-edit actions (work regardless of edit mode) ──

  /** Reveal the inline confirm bar. Non-recurring shows Delete/Cancel; a
      recurring todo with a due date shows Just this one / Entire series /
      Cancel so the user can pick scope. */
  const handleDeleteClick = useCallback(() => {
    if (!onUpdate || deleting) return;
    setConfirmingDelete(true);
  }, [onUpdate, deleting]);

  const handleConfirmDeleteSeries = useCallback(() => {
    if (!onUpdate || deleting) return;
    setDeleting(true);
    onUpdate({ id: todo.id, deleted: true });
  }, [onUpdate, todo.id, deleting]);

  /** Skip just this occurrence — advances due_at to the next rule match on
      the server, leaving the series alive. */
  const handleConfirmSkipInstance = useCallback(() => {
    if (!onUpdate || deleting) return;
    setDeleting(true);
    onUpdate({ id: todo.id, skip: true });
  }, [onUpdate, todo.id, deleting]);

  const handleCancelDelete = useCallback(() => {
    setConfirmingDelete(false);
  }, []);


  // Field controls write straight to local state. Save batches the diff;
  // Cancel discards. There is no immediate-save trapdoor any more.

  const isCompleted = todo.status === 'done';
  // Scope picker only makes sense for a still-actionable recurring row with a
  // due date; skip advances due_at, which has no meaning for a done todo.
  const canSkipRecurring = !!todo.recurrence_rule && !!todo.due_at && !isCompleted;

  // Local pre-flight against `rule.validation.todo.can_be_blocked`. The
  // server is authoritative (it re-evaluates inside the mark-blocked
  // transaction), but we disable the button when the rule is loaded and
  // returns false so the affordance matches reality. A null result means
  // the rule cache hasn't synced yet — keep the button enabled and let
  // the server gate, same pattern as the create-todo flow.
  const canBeBlockedRaw = useMemo(
    () => evalValidationRule('rule.validation.todo.can_be_blocked', todo),
    [todo],
  );
  // Treat null (cold start) as "show as enabled". Treat false as disabled.
  const canBeBlocked = canBeBlockedRaw !== false;
  const [markingBlocked, setMarkingBlocked] = useState(false);
  const handleMarkBlocked = useCallback(() => {
    if (!canBeBlocked || markingBlocked) return;
    setMarkingBlocked(true);
    dispatch(markBlockedRequested(todo.id));
    // The saga response lands asynchronously — close the modal so the
    // toast (rendered at the app shell) is visible. Matches the
    // delete-confirm path which calls onClose after dispatching.
    if (onClose) onClose();
  }, [canBeBlocked, markingBlocked, dispatch, todo.id, onClose]);

  return (
    <div className="todo-detail" role="region" aria-label="Todo details">
      <div className="todo-detail__header">
        {editingFields.has('title') ? (
          <>
            <span className="sr-only">{todo.title}</span>
            <MentionInput
              as="input"
              inputClassName="todo-detail__title-input"
              value={localTitle}
              onChange={setLocalTitle}
              aria-label="Todo title"
              autoFocus
            />
          </>
        ) : (
          <h2
            className={`todo-detail__title${isCompleted ? ' todo-detail__title--completed' : ''}${onUpdate ? ' todo-detail__title--editable' : ''}`}
            onClick={() => startEdit('title')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                startEdit('title');
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Edit title: ${todo.title}`}
          >
            <MentionText text={todo.title} />
          </h2>
        )}

        {/* Save commits the batched draft. Only renders when there is a
            change to save — otherwise the modal is effectively a viewer
            and the only exit is the close affordance (X / Esc / backdrop),
            which prompts when changes are pending. Hidden while the scope
            picker is up so the user can't bypass the instance/series pick. */}
        {canCommit && hasPendingChanges && !pendingScopedUpdate && (
          <button
            type="button"
            className="todo-detail__save"
            onClick={handleSave}
            aria-label="Save changes"
          >
            Save
          </button>
        )}

      </div>

        <div className="todo-detail__content">
          <DetailSlot slot="below-title" todo={todo} onUpdate={onUpdate} />

          {/* Confirmation strip — only renders when an action awaits user
              choice (Save on a recurring todo → instance/series picker, or
              Delete on any todo → confirm). The persistent action affordance
              (Delete) lives at the bottom of the modal; mounting it here
              would push the field list down for no reason. */}
          {(pendingScopedUpdate || confirmingDelete) && (
            <div className="todo-detail__actions-bar" data-field="actions">
              {pendingScopedUpdate ? (
                <div className="todo-detail__confirm" role="group" aria-label="Apply edit to">
                  <span className="todo-detail__confirm-label">Apply edit to:</span>
                  <button
                    type="button"
                    className="todo-detail__confirm-btn"
                    onClick={() => commitScopedUpdate('instance')}
                  >
                    Just this one
                  </button>
                  <button
                    type="button"
                    className="todo-detail__confirm-btn todo-detail__confirm-btn--danger"
                    onClick={() => commitScopedUpdate('series')}
                  >
                    Entire series
                  </button>
                  <button
                    type="button"
                    className="todo-detail__confirm-btn"
                    onClick={cancelScopedUpdate}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="todo-detail__confirm" role="group" aria-label="Confirm delete">
                  <span className="todo-detail__confirm-label">
                    {canSkipRecurring ? 'Delete which?' : 'Delete this todo?'}
                  </span>
                  {canSkipRecurring ? (
                    <>
                      <button
                        type="button"
                        className="todo-detail__confirm-btn"
                        onClick={handleConfirmSkipInstance}
                        disabled={deleting}
                      >
                        Just this one
                      </button>
                      <button
                        type="button"
                        className="todo-detail__confirm-btn todo-detail__confirm-btn--danger"
                        onClick={handleConfirmDeleteSeries}
                        disabled={deleting}
                      >
                        Entire series
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="todo-detail__confirm-btn todo-detail__confirm-btn--danger"
                      onClick={handleConfirmDeleteSeries}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="todo-detail__confirm-btn"
                    onClick={handleCancelDelete}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}

          <DetailSlot slot="below-actions" todo={todo} onUpdate={onUpdate} />

          {/* Description */}
          <div className="todo-detail__field" data-field="description">
            <label className="todo-detail__label">Description</label>
            {editingFields.has('description') ? (
              <MentionInput
                as="textarea"
                inputClassName="todo-detail__input todo-detail__textarea"
                value={localDescription}
                onChange={setLocalDescription}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSave();
                  }
                }}
                aria-label="Description"
                rows={3}
                autoFocus
              />
            ) : todo.description ? (
              <div
                className={`todo-detail__description-rendered${onUpdate ? ' todo-detail__value--editable' : ''}`}
                onClick={() => startEdit('description')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    startEdit('description');
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ href, children, ...props }) => {
                      // Mention links use a `kind:id` href scheme. Detect and
                      // render the provider chip instead of a raw <a>.
                      const mentionMatch = href?.match(/^([a-z_]+):([^)]+)$/);
                      if (mentionMatch) {
                        const [, kind, id] = mentionMatch;
                        const rawLabel = Array.isArray(children)
                          ? children.join('')
                          : String(children ?? '');
                        const label = rawLabel.startsWith('@')
                          ? rawLabel.slice(1)
                          : rawLabel;
                        return (
                          <MentionText text={`[@${label}](${kind}:${id})`} />
                        );
                      }
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          {...props}
                        >
                          {children}
                        </a>
                      );
                    },
                  }}
                >
                  {autoLinkText(todo.description)}
                </ReactMarkdown>
              </div>
            ) : (
              <span
                className="todo-detail__value todo-detail__value--editable"
                onClick={() => startEdit('description')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    startEdit('description');
                  }
                }}
                role="button"
                tabIndex={0}
              >
                No description
              </span>
            )}
          </div>

          <DetailSlot slot="below-description" todo={todo} onUpdate={onUpdate} />

          {/* Recurrence */}
          <div className="todo-detail__field" data-field="recurrence">
            <label className="todo-detail__label">Recurrence</label>
            <RecurrencePicker
              value={localRecurrenceRule}
              disabled={!onUpdate}
              onChange={setLocalRecurrenceRule}
              anchorDate={localDueAt}
            />
          </div>

          {/* Notifications. When enabled, the inline amount + unit
              controls ARE the value — don't render the formatted text
              alongside them or "Notify 2 minutes before" sits next to
              its own editor and the row wraps awkwardly. When disabled,
              show "Disabled" so the row isn't empty next to the toggle. */}
          <div className="todo-detail__field" data-field="notifications">
            <label className="todo-detail__label">Notifications</label>
            {localNotifyEnabled ? (
              (() => {
                const { amount, unit } = msToAmountUnit(localNotifyBefore || 0);
                return (
                  <span className="todo-detail__value todo-detail__value--inline-edit">
                    Notify
                    <input
                      className="todo-detail__input todo-detail__input--notify-amount"
                      type="number"
                      min={1}
                      step={1}
                      value={amount}
                      onChange={(e) => {
                        const next = parseInt(e.target.value, 10);
                        if (!Number.isFinite(next) || next < 1) return;
                        setLocalNotifyBefore(amountUnitToMs(next, unit));
                      }}
                      disabled={!onUpdate}
                      aria-label="Notification amount"
                    />
                    <select
                      className="todo-detail__select"
                      value={unit}
                      onChange={(e) =>
                        setLocalNotifyBefore(amountUnitToMs(amount, e.target.value as NotifyUnit))
                      }
                      disabled={!onUpdate}
                      aria-label="Notification unit"
                    >
                      <option value="minutes">minutes</option>
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                    before
                  </span>
                );
              })()
            ) : (
              <span className="todo-detail__value">Disabled</span>
            )}
            <button
              className="todo-detail__toggle-btn"
              onClick={() => {
                if (!localNotifyEnabled) {
                  setLocalNotifyEnabled(true);
                  if (!localNotifyBefore || localNotifyBefore === 0) {
                    setLocalNotifyBefore(120000);
                  }
                } else {
                  setLocalNotifyEnabled(false);
                }
              }}
              disabled={!onUpdate}
              aria-label={localNotifyEnabled ? 'Disable notifications' : 'Enable notifications'}
            >
              {localNotifyEnabled ? 'Disable' : 'Enable'}
            </button>
          </div>

          {/* Vanishing — only meaningful when there is a due date. Without
              one, ShouldVanish falls back to CreatedAt and silently auto-closes
              open-ended todos; backend NormalizeVanishing() strips the flag on
              the same invariant. Reactively hidden when due_at is cleared. */}
          {(localDueAt || todo.due_at) && (
            <div className="todo-detail__field" data-field="vanishing">
              <label className="todo-detail__label">Vanishing</label>
              <span className="todo-detail__value">
                {localVanishingEnabled
                  ? `Vanishing enabled${todo.vanish_after_hours ? ` \u2014 vanish after ${todo.vanish_after_hours} hours` : ''}`
                  : 'Vanishing disabled'}
              </span>
              <button
                className="todo-detail__toggle-btn"
                onClick={() => setLocalVanishingEnabled((v) => !v)}
                disabled={!onUpdate}
                aria-label={localVanishingEnabled ? 'Disable vanishing' : 'Enable vanishing'}
              >
                {localVanishingEnabled ? 'Disable' : 'Enable'}
              </button>
            </div>
          )}

          {/* Due Date */}
          <div className="todo-detail__field" data-field="due-date">
            <label className="todo-detail__label" id="todo-detail-due-date-label">Due date</label>
            {editingFields.has('due_at') ? (
              <DateTimeField
                mode="datetime"
                value={utcIsoToLocalInputValue(localDueAt)}
                onChange={(v) => {
                  setLocalDueAt(v ? localInputValueToUtcIso(v) : null);
                  setDueAtTouched(true);
                }}
                autoOpen
                ariaLabel="Due date"
              />
            ) : (
              <div className="todo-detail__value-row">
                <span
                  className={`todo-detail__value${onUpdate ? ' todo-detail__value--editable' : ''}`}
                  onClick={() => startEdit('due_at')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      startEdit('due_at');
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-labelledby="todo-detail-due-date-label"
                  aria-label={`Edit due date: ${formatDueDate(localDueAt, dayStartHour)}`}
                >
                  {formatDueDate(localDueAt, dayStartHour)}
                </span>
                {/* One-click clear for non-recurring (incl. project) tasks. Takes
                    effect on Save (sets due_at=null via pendingUpdate). Recurring
                    todos are excluded — clearing a series due needs scope choice. */}
                {onUpdate && localDueAt && !todo.recurrence_rule && (
                  <button
                    type="button"
                    className="todo-detail__due-clear"
                    aria-label="Remove due date"
                    title="Remove due date"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLocalDueAt(null);
                      setDueAtTouched(true);
                      setEditMode(true);
                    }}
                  >
                    &times;
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Priority */}
          <div className="todo-detail__field" data-field="priority">
            <label className="todo-detail__label">Priority</label>
            <select
              className="todo-detail__select"
              value={localPriority}
              onChange={(e) => setLocalPriority(e.target.value as TodoPriority)}
              disabled={!onUpdate}
              aria-label="Priority"
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Tags — household-wide free-form labels. Editable rows (onUpdate
              present) get the TagEditor with autocomplete; read-only rows
              render the applied tags as chips. Tags persist on Save via the
              `tags` key in pendingUpdate (server replace-sets). */}
          <div className="todo-detail__field" data-field="tags">
            <label className="todo-detail__label">Tags</label>
            {onUpdate ? (
              <TagEditor tags={localTags} onChange={setLocalTags} />
            ) : todo.tags && todo.tags.length > 0 ? (
              <span className="todo-detail__value todo-detail__tags">
                {todo.tags.map((t) => (
                  <TagChip key={tagKey(t)} label={t} />
                ))}
              </span>
            ) : (
              <span className="todo-detail__value">No tags</span>
            )}
          </div>

          {/* Assignment — show display name, not UUID. Editable when the
              current user can change the assignee: either they can edit the
              todo (owner, via onUpdate) or the row is claimable by them (e.g. a
              partner-owned project task). Rendering is identical for every todo
              — claimability, not project-ness, drives editability. */}
          <div className="todo-detail__field" data-field="assignment">
            <label className="todo-detail__label">Assigned to</label>
            {editingFields.has('owner_id') ? (
              <select
                className="todo-detail__select"
                value={localOwnerId}
                onChange={(e) => setLocalOwnerId(e.target.value || '')}
                aria-label="Assignment"
              >
                {/* Unassigning is an owner-only edit (UpdateTodo owner_id="").
                    The claim path needs a concrete new owner, so omit it when
                    the current user is reassigning a row they don't own. */}
                {isOwner && <option value="">Unassigned</option>}
                {uniqueMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.display_name}
                  </option>
                ))}
              </select>
            ) : todo.owner_id ? (
              <span
                className={`todo-detail__value${canEditAssignment ? ' todo-detail__value--editable' : ''}`}
                onClick={() => startEdit('owner_id')}
              >
                {getOwnerName(todo.owner_id)}
              </span>
            ) : (
              <span
                className={`todo-detail__value${canEditAssignment ? ' todo-detail__value--editable' : ''}`}
                onClick={() => startEdit('owner_id')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    startEdit('owner_id');
                  }
                }}
                role="button"
                tabIndex={0}
              >
                (Unassigned)
              </span>
            )}
          </div>

          <DetailSlot slot="below-assignment" todo={todo} onUpdate={onUpdate} />

          {/* Visibility — public (household-wide, default) vs private (only the
              current owner can see or act on it). Backend filters reads and
              rejects non-owner claims server-side; this is the affordance to
              flip the bit. Immediate-save like notifications/vanishing. */}
          <div className="todo-detail__field" data-field="visibility">
            <label className="todo-detail__label">Visibility</label>
            <span className="todo-detail__value">
              {localVisibility === 'private' ? 'Just me (private)' : 'Visible to household'}
            </span>
            <button
              className="todo-detail__toggle-btn"
              onClick={() =>
                setLocalVisibility(localVisibility === 'private' ? 'public' : 'private')
              }
              disabled={!onUpdate}
              aria-label={
                localVisibility === 'private' ? 'Make public' : 'Make private'
              }
            >
              {localVisibility === 'private' ? 'Make public' : 'Make private'}
            </button>
          </div>

          {/* Close Reason (only for closed todos) */}
          {todo.close_reason && (
            <div className="todo-detail__field" data-field="close-reason">
              <label className="todo-detail__label">Close reason</label>
              <span className="todo-detail__value">
                {todo.close_reason === 'completed' ? 'Completed' : 'Vanished'}
              </span>
            </div>
          )}

          {/* Source field intentionally removed (Issue #4) */}

          {/* Delete sits at the bottom — destructive, infrequent action.
              Hidden during the inline confirm flows so the action bar
              above this region is the user's only path. Mark blocked
              groups with Delete here: both terminal actions on the
              original todo. Disabled when the validation rule says no
              (recurring / already completed). */}
          {onUpdate && !pendingScopedUpdate && !confirmingDelete && (
            <div className="todo-detail__footer">
              <button
                type="button"
                className="todo-detail__block-btn"
                onClick={handleMarkBlocked}
                disabled={!canBeBlocked || markingBlocked}
                aria-label="Mark todo as blocked"
                title={
                  canBeBlocked
                    ? undefined
                    : 'Recurring or already-completed todos cannot be marked blocked.'
                }
              >
                {markingBlocked ? 'Marking…' : 'Mark blocked'}
              </button>
              <button
                className="todo-detail__delete-btn"
                onClick={handleDeleteClick}
                disabled={deleting}
                aria-label="Delete todo"
              >
                Delete
              </button>
            </div>
          )}
        </div>
    </div>
  );
}
